import React, { useEffect, useState } from 'react';
import { TableRow } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { isNumber } from 'lodash/fp';
import { useFromToContext } from '../../../shared';
import { SortOrder, useTableStatisticsQuery } from '../../../../generated/graphql';
import { DEFAULT_TABLE_ROW_HEIGHT, LoaderRow, Ordering, TableCell } from '../../../../ui-kit';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { useUser } from '../../../../utils/hooks/auth';
import NestedCollapsibleTable from '../NestedCollapsibleTable';
import { TableWithCollapsibleContent } from '../style';
import { getSortFunction, getTableDataMaps, headCells } from './utils';
import { OrderColumn, Row, RowData } from './types';
import TableCellContent from './TableCellContent';
import TableHeadCells from './TableHead';

const TableStatisticsTable = () => {
    const { dateFromTo } = useFromToContext();
    const { user: { defaultCurrency: { currencyCode } } } = useUser();
    const { t } = useTranslation();
    const [collapseMap, setCollapseMap] = useState<Record<string, boolean>>({});
    const { priceService } = usePriceService();

    const { data, loading } = useTableStatisticsQuery({ variables: { dateFromTo } });

    const tableStatistics = data ? data.tableStatistics : [];

    const setOpen = (id: string) => {
        const updatedCollapseMap = { ...collapseMap, [id]: !collapseMap[id] };
        setCollapseMap(updatedCollapseMap);
    };

    const [ordering, setOrdering] = useState<[OrderColumn | null, SortOrder | false]>(['grossRevenue', SortOrder.Desc]);
    const [orderingColumn, orderDirection] = ordering;
    const grossRevenueSort = orderingColumn === 'grossRevenue' ? orderDirection : false;
    const averageBillSort = orderingColumn === 'averageBill' ? orderDirection : false;
    const billCountSort = orderingColumn === 'billCount' ? orderDirection : false;

    const setNewSort = (column: OrderColumn) => (sortDirection: SortOrder | false) => {
        setOrdering([sortDirection ? column : null, sortDirection]);
    };

    const setGrossRevenue = setNewSort('grossRevenue');
    const setAverageBillSort = setNewSort('averageBill');
    const setBillCountSort = setNewSort('billCount');

    const finalOrderDirection = orderDirection ? orderDirection.toLowerCase() as Ordering : 'asc';
    const finalOrderingColumn = orderingColumn || 'name';


    const sortFunction = getSortFunction(finalOrderDirection, finalOrderingColumn);
    const { sectionsRowData, placeSectionMap } = getTableDataMaps(tableStatistics, priceService, currencyCode);
    const sortedSectionsRowData = sortFunction(sectionsRowData);

    const getRowFromRowData = (row: RowData): Row => ({
        id: row.id,
        name: row.name,
        grossRevenue: isNumber(row.grossRevenue)
            ? priceService.toLocaleString(row.grossRevenue, currencyCode)
            : t('N/A'),
        averageBill: isNumber(row.averageBill)
            ? priceService.toLocaleString(row.averageBill, currencyCode)
            : t('N/A'),
        billCount: row.billCount,
    });

    useEffect(() => {
        if (!loading) {
            if (sectionsRowData.length === 1) {
                setCollapseMap({ [sectionsRowData[0].id]: true });
            } else {
                setCollapseMap({});
            }
        }
    // eslint-disable-next-line
    }, [loading]);

    const columnCount = headCells.length;

    return (
        <TableWithCollapsibleContent
            TableHeadCellComponent={TableHeadCells}
            customHeadCellProps={{
                grossRevenueSort, setGrossRevenue,
                averageBillSort, setAverageBillSort,
                billCountSort, setBillCountSort
            }}
            fixedLayout
            headCells={headCells}
        >
            {loading
            ? <LoaderRow columnCount={columnCount} itemsPerPage={5} itemHeight={DEFAULT_TABLE_ROW_HEIGHT} />
            : sortedSectionsRowData.map((sectionRow, sectionRowIndex) => {
                const parentRow = getRowFromRowData(sectionRow);
                const isParentRowEven = sectionRowIndex % 2 === 0;

                const orderedPlacesRowData = sortFunction(placeSectionMap[sectionRow.id]);
                return (
                    <React.Fragment key={sectionRow.id}>
                        <TableRow className={isParentRowEven ? undefined : 'stripedRow'}>
                            {headCells.map((headCell, index) => (
                                <TableCell isSticky={index === 0} align={headCell.align} key={`${sectionRow.id}_${headCell.id}`}>
                                    <TableCellContent
                                        id={headCell.id}
                                        align={headCell.align}
                                        content={parentRow[headCell.id]}
                                        hasCollapsibleContent
                                        childCount={orderedPlacesRowData.length}
                                        isOpen={collapseMap[sectionRow.id]}
                                        setIsOpen={() => setOpen(sectionRow.id)}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell padding='none' style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
                                <NestedCollapsibleTable isOpen={collapseMap[sectionRow.id]} startOdd={!isParentRowEven}>
                                    {orderedPlacesRowData.map(placeRowData => {
                                        const placeRow = getRowFromRowData(placeRowData);
                                        return (
                                            <TableRow key={placeRow.id}>
                                                {headCells.map((headCell, index) => (
                                                    <TableCell
                                                        key={`${sectionRow.id}_${headCell.id}`}
                                                        isSticky={index === 0}
                                                        align={headCell.align}
                                                    >
                                                        <TableCellContent
                                                            id={headCell.id}
                                                            align={headCell.align}
                                                            content={placeRow[headCell.id]}
                                                            isChild
                                                        />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </NestedCollapsibleTable>
                            </TableCell>
                        </TableRow>
                    </React.Fragment>
                );
            })}
        </TableWithCollapsibleContent>
    );
};

export default TableStatisticsTable;